/**
 * The filters a saved report is scoped by, rendered with the studio's compact filter control. Show By
 * and zero suppression sit alongside because they too re-scope this session only, and render on every
 * report, filtered or not.
 */

import { splitHierarchyPairId } from 'src/components/star-grid/services/filterHierarchyService';
import React, { useMemo } from 'react';
import Select from '@amzn/awsui-components-core/select';
import Toggle from '@amzn/awsui-components-core/toggle';
import type { ReportFilter, ViewControl } from 'src/components/star-grid/api/reportEngineTypes';
import { ReportParameterField } from 'src/components/star-grid/components/ReportParameterField';
import { ReportViewControls } from 'src/components/star-grid/components/ReportViewControls';
import { ReportFilterField } from 'src/components/star-grid/components/ReportFilterField';
import { loadFilterHierarchy } from 'src/components/star-grid/services/filterHierarchyService';
import type { StarGridText } from 'src/components/star-grid/hooks/useStarGridText';
import { useReportFilterParameters } from 'src/components/star-grid/hooks/useReportFilterParameters';
import { orderFilterBarItems, visibleReportFilters } from 'src/components/star-grid/utils/reportFilterBar';
import { asShowBy, showByOptions, type ShowBy } from 'src/components/star-grid/utils/showBy';
import styles from 'src/components/star-grid/components/styles/SavedReportFilters.module.scss';

export interface ReportFiltersProps {
  readonly filters: readonly ReportFilter[];
  /** Per filter pair, the hierarchy a dataset report's picker shows instead of the P&L one. */
  readonly filterTrees?: Readonly<Record<string, string>>;
  /** Deck/view controls (Version, Deck, Scale). Shown first, ahead of the dimension filters. */
  readonly viewControls?: readonly ViewControl[];
  /** Options + handler per view-control id, for the ones that can actually act. See `ReportViewControls`. */
  readonly viewControlActions?: Readonly<
    Record<string, { options: readonly { value: string; label: string }[]; onChange: (value: string) => void; value?: string }>
  >;
  /** Controls rendered by their own component rather than a dropdown — `Version`. See `ReportViewControls`. */
  readonly viewControlNodes?: Readonly<Record<string, React.ReactNode>>;
  /** The bundle's own keyed lookup, so a missing key is a compile error rather than blank text. */
  readonly text: StarGridText;
  /** Members chosen this session, keyed by dimension. Absent leaves the report's own scope. */
  readonly overrides: Readonly<Record<string, readonly string[]>>;
  readonly onChange: (dimension: string, members: readonly string[]) => void;
  /** True while a query is running, so the controls cannot queue conflicting re-runs. */
  readonly busy?: boolean;
  /** The grain the grid is showing. Absent before the first response reports one. */
  readonly showBy?: ShowBy;
  /**
   * Grains this report's periods can honour. A grain outside it is offered DISABLED rather than
   * hidden, so the picker still shows the full vocabulary — every current deck screen states a single
   * quarter, so `yearly` is unavailable on all of them and silently omitting it would look like a bug.
   */
  readonly availableShowBy?: readonly string[];
  readonly onShowByChange: (next: ShowBy) => void;
  readonly suppressZeroRows: boolean;
  readonly onSuppressZeroRowsChange: (next: boolean) => void;
}

export const ReportFilters: React.FC<ReportFiltersProps> = ({
  filters,
  filterTrees,
  viewControls,
  viewControlActions,
  viewControlNodes,
  text,
  overrides,
  onChange,
  busy,
  showBy,
  availableShowBy,
  onShowByChange,
  suppressZeroRows,
  onSuppressZeroRowsChange
}) => {
  // Hidden filters (Country / Product on the WW Report) still scope the query; they just get no chip.
  const visibleFilters = useMemo(() => visibleReportFilters(filters), [filters]);
  const { parameters } = useReportFilterParameters(visibleFilters, filterTrees);
  const items = useMemo(() => orderFilterBarItems(viewControls ?? [], visibleFilters), [viewControls, visibleFilters]);
  const options = showByOptions(text, availableShowBy);
  return (
    <section id="stargrid-report-view-filters" className={styles.filters} aria-label={text('reportFilters')}>
      {/* In the deck's own order: controls and filters interleave by the screen entry they came from. */}
      {items.map((item) => {
        if (item.kind === 'control')
          return (
            <ReportViewControls
              key={`control-${item.control.id}`}
              controls={[item.control]}
              interactive={viewControlActions}
              custom={viewControlNodes}
            />
          );
        const parameter = parameters[item.index];
        if (!parameter) return null;
        return (
          <div key={parameter.key} className={styles.parameter}>
            <ReportParameterField
              compact
              parameter={parameter}
              // By pair (the chip's own key) or by bare dimension (Filter by on a row), whichever was set.
              values={overrides[parameter.key] ?? overrides[splitHierarchyPairId(parameter.key)?.dimension ?? ''] ?? parameter.default}
              disabled={busy}
              // By PAIR, so every filter gets the hierarchy panel and its member-subset modal rather
              // than only the ~8 pairs registered with a hierarchy id.
              loadHierarchy={loadFilterHierarchy}
              onChange={(dimension, values) => onChange(dimension, values)}
            />
          </div>
        );
      })}
      <div className={styles.parameter}>
        {/* Wrapped in the SAME chip as the dimension filters. A bare Cloudscape Select has none of the
            bar's chrome, so Show By stood out as a different kind of control beside them. */}
        <ReportFilterField label={text('showBy')} controlId="stargrid-show-by" value={showBy ?? ''} dropdown={false}>
          <Select
            controlId="stargrid-show-by"
            // Portals the panel to the body. The filter bar clips overflow, so the dropdown opened
            // INSIDE it and never appeared — the same cause as the deck picker in the line set.
            expandToViewport
            selectedOption={options.find((option) => option.value === showBy) ?? null}
            options={options}
            disabled={busy}
            placeholder={text('showBy')}
            ariaLabel={text('showBy')}
            /* Narrowed rather than cast: an option's `value` is optional in Cloudscape's own type, and
             a grain the vocabulary does not know is dropped instead of re-queried blindly. */
            onChange={({ detail }) => {
              const next = asShowBy(detail.selectedOption.value);
              if (next) onShowByChange(next);
            }}
          />
        </ReportFilterField>
      </div>
      <div className={styles.parameter}>
        <Toggle checked={suppressZeroRows} disabled={busy} onChange={({ detail }) => onSuppressZeroRowsChange(detail.checked)}>
          {text('suppressZeroRows')}
        </Toggle>
      </div>
    </section>
  );
};
